import React, { useState } from 'react';
import { View, ScrollView, ImageBackground, Animated, Platform } from 'react-native';
import { useNavigation } from '@react-navigation/native';
import { globalStyles } from '../styles/globalStyles';
import { VenueHeader } from '../components/venue-submission/VenueHeader';
import { VenueDetailsForm } from '../components/venue-submission/VenueDetailsForm';
import { NavigationButtons } from '../components/venue-submission/NavigationButtons';
import { getFirestore, collection, addDoc } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { BackgroundVideo } from '../components/BackgroundVideo';
import { useAnimation } from '../hooks/useAnimation';
import { venueSubmissionStyles } from '../styles/venueSubmissionStyles';

const formFields = [
  {
    section: 1,
    fields: [
      { label: "State", value: "", type: "text" },
      { label: "City", value: "", type: "text" },
      { label: "Address", value: "", type: "text" },
      { label: "ZipCode", value: "", type: "text" },
      { label: "Website", value: "", type: "url" },
      { label: "Capacity", value: "", type: "number" }
    ]
  },
  {
    section: 2,
    fields: [
      { label: "Stage", placeholder: "enter stage details...", type: "text" },
      { label: "Power", placeholder: "enter Power details...", type: "text" },
      { label: "Lighting", placeholder: "enter Lighting details...", type: "text" },
      { label: "Sound", placeholder: "enter Sound details...", type: "text" },
      { label: "Notes", placeholder: "enter any additional notes...", type: "text" }
    ]
  }
];

export function VenueSubmission({ route }) {
  const navigation = useNavigation();
  const [formData, setFormData] = useState(formFields);
  const venueName = route.params?.venueName || "";
  const fadeIn = useAnimation(0, 1, 700);
  const slideUp = useAnimation(30, 0, 700);

  const handleFieldChange = (section, label, value) => {
    setFormData(prevData => {
      const newData = [...prevData];
      const sectionIndex = newData.findIndex(s => s.section === section);
      const fieldIndex = newData[sectionIndex].fields.findIndex(f => f.label === label);
      newData[sectionIndex].fields[fieldIndex].value = value;
      return newData;
    });
  };

  const handleBack = () => {
    navigation.goBack();
  };

  const handleNext = async () => {
    try {
      const auth = getAuth();
      const db = getFirestore();
      const functions = getFunctions();

      if (!auth.currentUser) {
        alert('You must be logged in to submit a venue');
        return;
      }

      if (!venueName) {
        alert('Please enter a venue name');
        return;
      }

      // Convert form data to submission object
      const submissionData = {
        venueName: venueName,
        createdAt: new Date(),
        status: 'pending',
        userId: auth.currentUser.uid,
        userEmail: auth.currentUser.email,
        submitterName: auth.currentUser.displayName,
        submitterEmail: auth.currentUser.email,
        submittedBy: auth.currentUser.email
      };

      // Add all field values to submission data
      formData.forEach(section => {
        section.fields.forEach(field => {
          submissionData[field.label.toLowerCase()] = field.value;
        });
      });

      // Add to new_venues collection
      const docRef = await addDoc(collection(db, 'new_venues'), submissionData);

      // Send email notifications
      try {
        const handleVenueSubmission = httpsCallable(functions, 'handleVenueSubmission');
        await handleVenueSubmission({
          ...submissionData,
          id: docRef.id,
          name: venueName
        });
      } catch (emailError) {
        console.error('Error sending venue submission notifications:', emailError);
        // Don't throw here - we still want to proceed even if email fails
      }

      alert('Venue submitted successfully! It will be reviewed by our team.');
      navigation.navigate('MyReports');
    } catch (error) {
      console.error('Error submitting venue:', error);
      alert('Error submitting venue. Please try again.');
    }
  };

  return (
    <View style={globalStyles.container}>
      <BackgroundVideo />
      <Animated.View 
        style={[
          globalStyles.overlay,
          {
            opacity: fadeIn,
            transform: [{ translateY: slideUp }]
          }
        ]}
      >
        <Animated.View 
          style={[
            venueSubmissionStyles.formContainer,
            Platform.OS === 'web' && {
              transition: 'all 0.3s ease-in-out',
              ':hover': {
                boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
              }
            }
          ]}
        >
          <ScrollView
            style={globalStyles.submissionContainer}
            showsVerticalScrollIndicator={false}
          >
            <VenueHeader title={venueName} />
            <View style={globalStyles.formContainer}>
              <VenueDetailsForm
                formFields={formData}
                onFieldChange={handleFieldChange}
              />
            </View>
            <NavigationButtons
              onBack={handleBack}
              onNext={handleNext}
            />
          </ScrollView>
        </Animated.View>
      </Animated.View>
    </View>
  );
} 